import { FC, useEffect } from 'react';
import Table from '@cloudscape-design/components/table';
import Button from '@cloudscape-design/components/button';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';

import { PreviewSessionTableConstants } from 'src/constants';
import { PreviewSessionEntry, PreviewSessionStatus } from './types';
import styles from './PreviewSessionTable.module.css';

export interface PreviewSessionTableProps {
  sessions: PreviewSessionEntry[];
  onTerminatePreview: (deviceId: string) => void;
  onMount?: () => void;
}

const { LABELS, COLUMN_IDS } = PreviewSessionTableConstants;

export const PreviewSessionTable: FC<PreviewSessionTableProps> = ({
  sessions,
  onTerminatePreview,
  onMount,
}) => {
  useEffect(() => {
    onMount?.();
  }, [onMount]);

  return (
    <Table
      header={<Header>{LABELS.TITLE}</Header>}
      items={sessions}
      trackBy={COLUMN_IDS.DEVICE_ID}
      empty={
        <Box textAlign="center" color="text-body-secondary" padding="l">
          {LABELS.EMPTY}
        </Box>
      }
      stickyColumns={{ last: 1 }}
      contentDensity="compact"
      ariaLabels={{ tableLabel: LABELS.TITLE }}
      columnDefinitions={[
        {
          id: COLUMN_IDS.DEVICE_ID,
          header: LABELS.DEVICE_ID,
          cell: (item) => item.dsn || item.deviceId,
        },
        { id: COLUMN_IDS.CUSTOMER_ID, header: LABELS.CUSTOMER_ID, cell: (item) => item.customerId },
        { id: COLUMN_IDS.CREATIVE_ID, header: LABELS.CREATIVE_ID, cell: (item) => item.creativeId },
        {
          id: COLUMN_IDS.EXPIRES_AT,
          header: LABELS.EXPIRES_AT,
          cell: (item) =>
            new Date(item.expiresAt).toLocaleString(undefined, { timeZoneName: 'short' }),
        },
        {
          id: COLUMN_IDS.ACTION,
          header: LABELS.ACTION,
          cell: (item) => {
            const isTerminating = item.status === PreviewSessionStatus.TERMINATING;
            return (
              <div className={styles.endButton}>
                <Button
                  variant="normal"
                  disabled={isTerminating}
                  loading={isTerminating}
                  onClick={() => onTerminatePreview(item.deviceId)}
                >
                  {isTerminating ? LABELS.TERMINATING : LABELS.TERMINATE}
                </Button>
              </div>
            );
          },
        },
      ]}
    />
  );
};
